/**
 * Per-product print technology on Product Sync (Phase 96): the reads and the
 * write behind `PrintTechnologyPicker` and `PrintTechnologyChip`.
 *
 * One org-wide `productPrintTechnology.list` read serves every row: React
 * Query dedupes it, and each row selects only its own product, so a save
 * re-renders that product's chip and picker, not the page. A save patches the
 * cache on `ok: true` (no refetch); a refusal or a thrown error leaves the
 * cache as it was and shows a sticky toast.
 *
 * RBAC: both reads need `org_settings.read` (the page's sidebar check and the
 * procedures' own check), so a caller without it sends no request and sees
 * nothing rather than a 403 dressed up as a load error. The write needs
 * `skus.manage`; the picker checks that before it offers the control.
 */
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useRole } from '@/hooks/use-role';
import { trpc } from '@/lib/trpc';

export type ProductPrintTechnology = 'DTG' | 'DTF';

type PrintTechnologyMap = Record<string, ProductPrintTechnology>;

/** Five minutes, matching the mappings read it sits beside. */
const STALE_MS = 300_000;

/** One sticky error per product: a second failure replaces the first, a later save clears it. */
export function saveErrorToastId(productId: string): string {
  return `product-print-technology-save:${productId}`;
}

function withPick(
  map: PrintTechnologyMap,
  productId: string,
  pick: ProductPrintTechnology | null,
): PrintTechnologyMap {
  const next = { ...map };
  if (pick) next[productId] = pick;
  else delete next[productId];
  return next;
}

/**
 * The stored pick for one product: `DTG`, `DTF`, or `null` for Default (no
 * row). `data` is `undefined` while loading, on error, or when the caller
 * cannot read Product Sync settings (`enabled: false`).
 */
export function useProductPrintTechnology(productId: string) {
  const { can } = useRole();
  const select = useCallback(
    (data: { byProductId: PrintTechnologyMap }) => data.byProductId[productId] ?? null,
    [productId],
  );
  return trpc.productPrintTechnology.list.useQuery(undefined, {
    staleTime: STALE_MS,
    enabled: can('org_settings.read'),
    select,
  });
}

/**
 * True when the org maps the Shopify print_technology metafield (BLU-676).
 * Same query and staleTime as `BatchEditVariantsModal`, so they share one
 * cache entry. Fails open: false while loading or on error, so no hint.
 */
export function usePrintTechMapped(): boolean {
  const { can } = useRole();
  const query = trpc.shopifyMetafieldMappings.list.useQuery(undefined, {
    staleTime: STALE_MS,
    enabled: can('org_settings.read'),
    select: (rows) => rows.some((m) => m.purpose === 'print_technology'),
  });
  return query.data ?? false;
}

/**
 * Save one product's pick (`null` = Default). The cache patch and the toast
 * sit on the mutation itself, not on `mutate`, so they still run when the row
 * is collapsed (the picker unmounted) before the save lands.
 */
export function useSetProductPrintTechnology() {
  const { t } = useTranslation('productSync');
  const utils = trpc.useUtils();

  const showSaveError = (productId: string) => {
    toast.error(t('printTechnology.saveError'), { id: saveErrorToastId(productId), duration: Infinity });
  };

  return trpc.productPrintTechnology.set.useMutation({
    onMutate: async () => {
      // A background list read already in flight could land after the patch
      // below and put the old value back. Only when there is data to keep: a
      // first load cancelled here would sit idle with nothing to show.
      if (utils.productPrintTechnology.list.getData() !== undefined) {
        await utils.productPrintTechnology.list.cancel();
      }
    },
    onSuccess: (result, { productId, printTechnology }) => {
      if (!result.ok) {
        showSaveError(productId);
        return;
      }
      utils.productPrintTechnology.list.setData(undefined, (prev) =>
        prev ? { byProductId: withPick(prev.byProductId, productId, printTechnology) } : prev,
      );
      toast.dismiss(saveErrorToastId(productId));
    },
    onError: (_error, { productId }) => showSaveError(productId),
  });
}
